import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fontsource-variable/figtree';
import './index.css';
import { ErrorBoundary } from './ErrorBoundary';
import { AppProviders } from './app/AppProviders';
import { AppRoutes } from './app/AppRoutes';
import { startThemeColorSync } from './lib/themeColor';

// Optional Sentry error tracking — inert unless VITE_SENTRY_DSN was set at
// build time. Loaded dynamically so builds without a DSN never ship the SDK.
const sentryDsn = String(import.meta.env.VITE_SENTRY_DSN || '').trim();
if (sentryDsn) {
  import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        environment: import.meta.env.MODE,
        // Error tracking only — no performance tracing or session replay.
        tracesSampleRate: 0,
      });
    })
    .catch(() => { /* never block app boot on telemetry */ });
}
// PWA SW registration is auto-injected by vite-plugin-pwa
// Safety: allow disabling SW at runtime and auto-reload on updates
(function manageSW() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const url = new URL(location.href);
    if (url.searchParams.get('no-sw') === '1') localStorage.setItem('disablePwa', '1');
    const disabled = localStorage.getItem('disablePwa') === '1';
    if (disabled) {
      // Unregister all service workers and reload once
      navigator.serviceWorker.getRegistrations().then((regs) => {
        Promise.all(regs.map(r => r.unregister())).finally(() => {
          if (!(window as any).__sw_disabled__) {
            (window as any).__sw_disabled__ = true;
            location.replace(location.pathname + location.search.replace(/([?&])no-sw=1/,'$1').replace(/\?$/,''));
          }
        });
      });
      return;
    }
    // Notify app when a new version is available and let UI drive the refresh
    function markUpdateAvailable() {
      try { localStorage.setItem('updateAvailable', '1'); } catch {}
      try { window.dispatchEvent(new CustomEvent('app:update-available')); } catch {}
    }
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      if (reg.waiting) markUpdateAvailable();
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available
            markUpdateAvailable();
          }
        });
      });
    }).catch(()=>{});
  } catch {}
})();

// Keep --vh in sync when dynamic viewport units are unavailable.
// Also track visualViewport offset for fixed mobile headers.
const supportsDynamicVh = typeof window !== 'undefined' && typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height: 100dvh');
if (typeof window !== 'undefined') {
  let pendingVhFrame = false;
  let debounceTimer: number | undefined;
  const DEBOUNCE_MS = 120;
  function applyViewportVars() {
    const vv = window.visualViewport;
    const height = vv?.height ?? window.innerHeight;
    const top = vv?.offsetTop ?? 0;
    if (!supportsDynamicVh) {
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    document.documentElement.style.setProperty('--vv-top', `${top}px`);
  }
  function scheduleViewportVars() {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      if (pendingVhFrame) return;
      pendingVhFrame = true;
      window.requestAnimationFrame(() => {
        pendingVhFrame = false;
        applyViewportVars();
      });
    }, DEBOUNCE_MS);
  }
  scheduleViewportVars();
  window.addEventListener('resize', scheduleViewportVars, { passive: true });
  window.addEventListener('orientationchange', scheduleViewportVars, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleViewportVars, { passive: true });
    window.visualViewport.addEventListener('scroll', scheduleViewportVars, { passive: true });
  }
}

// Apply saved theme early (accent colors / backgrounds)
try {
  const saved = localStorage.getItem('theme') || 'emerald';
  document.documentElement.setAttribute('data-theme', saved);
  document.documentElement.setAttribute('data-bg', localStorage.getItem('bg') || 'graphite');
  const accent = localStorage.getItem('accent');
  if (accent) {
    document.documentElement.style.setProperty('--echo-accent', accent);
    // naive contrast pick
    const rgb = accent.replace('#','');
    const r=parseInt(rgb.slice(0,2),16), g=parseInt(rgb.slice(2,4),16), b=parseInt(rgb.slice(4,6),16);
    const l = 0.299*r + 0.587*g + 0.114*b;
    const fg = l > 150 ? '#061a13' : '#ffffff';
    document.documentElement.style.setProperty('--echo-accent-fg', fg);
  }
  const uiScale = parseFloat(localStorage.getItem('uiScale')||'1') || 1;
  // Only override the root size for a custom scale; at 1x the stylesheet owns it (see the compact-window rule in index.css).
  if (uiScale !== 1) document.documentElement.style.fontSize = `${Math.round(16*uiScale)}px`;
} catch {}
startThemeColorSync();

// Apply deep-link channel/space from URL query before app boot.
try {
  const url = new URL(location.href);
  const ch = url.searchParams.get('channelId');
  const sp = url.searchParams.get('spaceId') || url.searchParams.get('voidId');
  if (ch) localStorage.setItem('currentChannelId', ch);
  if (sp) localStorage.setItem('currentVoidId', sp);
  // ChatLayout discards dm_-prefixed space IDs on init to avoid stale DM auto-open.
  // Store DM deep links separately so they survive that guard and are applied post-connect.
  if (sp?.startsWith('dm_') && ch) {
    localStorage.setItem('pendingDmDeepLink', JSON.stringify({ spaceId: sp, channelId: ch }));
  }
} catch {}


ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppProviders>
        <AppRoutes />
      </AppProviders>
    </ErrorBoundary>
  </React.StrictMode>
);
